import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import apiEndpoints from '../../axios/api';
import InvoiceTemplateRenderer from '../SocietyPortal/Finance/Invoices/components/InvoiceTemplateRenderer';
import usePrintIsolation from '../../utils/usePrintIsolation';

// Local servers only: a phone can't reach "localhost", so use the host this page
// came from. A deployed API URL has no "localhost" in it and is left alone.
const apiBaseUrl = () => (process.env.REACT_APP_NODE_MOBILE_BASE_URL || '')
    .replace('localhost', window.location.hostname);

// The bill keeps its A4 layout and scales down to fit, so a phone shows the
// amounts without sideways scrolling. Printing always uses full size.
const A4_WIDTH_PX = 794;
const PAGE_PADDING_PX = 24;
const fitScale = () => Math.min(1, (window.innerWidth - PAGE_PADDING_PX) / A4_WIDTH_PX);

/**
 * A bill opened from a shared link, with no login. It deliberately avoids
 * axiosInstance, which adds the portal's token and sends a failed request to the
 * login page. "Save as PDF" comes from the browser's print dialog.
 */
const PublicBill = () => {
    const { token } = useParams();
    const [bill, setBill] = useState(null);
    const [failed, setFailed] = useState(false);
    const [scale, setScale] = useState(fitScale);

    const printRef = useRef(null);
    usePrintIsolation(printRef, !!bill);

    useEffect(() => {
        let active = true;
        axios.get(`${apiBaseUrl()}${apiEndpoints.PUBLIC_BILL_API}/${encodeURIComponent(token)}`)
            .then((res) => { if (active) setBill(res.data?.data || null); })
            .catch(() => { if (active) setFailed(true); });
        return () => { active = false; };
    }, [token]);

    useEffect(() => {
        const onResize = () => setScale(fitScale());
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // Browsers name the saved PDF after the page title.
    useEffect(() => {
        if (bill?.document_number) document.title = `Bill ${bill.document_number}`;
    }, [bill]);

    if (failed) {
        return (
            <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-gray-50 tw-p-6">
                <div className="tw-text-center">
                    <Icon icon="mdi:file-alert-outline" className="tw-text-5xl tw-text-gray-400 tw-mx-auto" />
                    <h1 className="tw-text-lg tw-font-bold tw-text-gray-800 tw-mt-3">Bill not found</h1>
                    <p className="tw-text-sm tw-text-gray-500 tw-mt-1">This link is not valid, or the bill has been withdrawn.</p>
                </div>
            </div>
        );
    }

    if (!bill) {
        return (
            <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-gray-50">
                <p className="tw-text-gray-500 tw-text-sm">Loading bill...</p>
            </div>
        );
    }

    return (
        <>
            <style>{`
                @media print {
                    body { margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    .no-print, .print-hide { display: none !important; }
                    .print-expand { height: auto !important; min-height: 0 !important; max-height: none !important; overflow: visible !important; }
                    .print-area { background: #fff !important; padding: 0 !important; min-height: 0 !important; overflow: visible !important; }
                    .print-page { width: 100% !important; zoom: 1 !important; margin: 0 !important; }
                    @page { size: A4; margin: 8mm; }
                }
            `}</style>

            <div className="no-print tw-bg-white tw-border-b tw-border-gray-200 tw-sticky tw-top-0 tw-z-10 tw-px-4 tw-py-3 tw-flex tw-items-center tw-justify-between tw-gap-3">
                <div className="tw-min-w-0">
                    <h1 className="tw-text-base tw-font-bold tw-text-gray-900 tw-m-0">Bill</h1>
                    <p className="tw-text-xs tw-text-gray-500 tw-m-0 tw-truncate">{bill.document_number}</p>
                </div>
                <button
                    onClick={() => window.print()}
                    className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-indigo-600 tw-text-white tw-text-sm tw-font-medium tw-rounded-lg tw-border-0 tw-whitespace-nowrap"
                >
                    <Icon icon="mdi:printer-outline" /> Print / Save PDF
                </button>
            </div>

            <div className="print-area tw-bg-gray-100 tw-min-h-screen tw-p-3">
                <div ref={printRef} className="print-page tw-bg-white tw-mx-auto" style={{ width: `${A4_WIDTH_PX}px`, zoom: scale }}>
                    <InvoiceTemplateRenderer invoice={bill} />
                </div>
            </div>
        </>
    );
};

export default PublicBill;
